import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterOptions, OpenDataIndicator } from '../../../interfaces/OpenData';
import { getAllIndicatorsForDownload, getFilterOptions } from './openDataThunk';

export interface FilterState {
    derechos: string[];
    tiposIndicadores: string[];
    dimensiones: string[];
    ods: string[];
    priorityGroups: string[];
    variables: string[];
    filterOptions: FilterOptions | null;
    selectedIndicators: OpenDataIndicator[];
    allIndicatorsCache: OpenDataIndicator[] | null;
    initialFilters: {
        derechos: string[];
        tiposIndicadores: string[];
        dimensiones: string[];
        ods: string[];
        priorityGroups: string[];
        variables: string[]
    };
    hasAppliedFilters: boolean;
    isLoadingAllIndicators: boolean;
    isAllSelected: boolean;
}

const initialState: FilterState = {
    derechos: [],
    tiposIndicadores: [],
    dimensiones: [],
    ods: [],
    priorityGroups: [],
    variables: [],
    filterOptions: null,
    initialFilters: {
        derechos: [],
        tiposIndicadores: [],
        dimensiones: [],
        ods: [],
        priorityGroups: [],
        variables: []
    },
    selectedIndicators: [],
    allIndicatorsCache: null,
    hasAppliedFilters: false,
    isLoadingAllIndicators: false,
    isAllSelected: false,
};

export const openDataFiltersSlice = createSlice({
    name: 'openDataFilters',
    initialState,
    reducers: {
        updateFilter: (
            state,
            action: PayloadAction<{ filterType: keyof Pick<FilterState, 'derechos' | 'tiposIndicadores' | 'dimensiones' | 'priorityGroups' | 'ods' | 'variables'>; values: string[] }>
        ) => {
            const { filterType, values } = action.payload;

            state[filterType] = values;

            if (filterType === 'derechos' && state.filterOptions?.dimensiones) {
                const selectedDerechos = values;
                state.dimensiones = state.dimensiones.filter(dimensionTitulo => {
                    const dim = state.filterOptions!.dimensiones.find(d => d.titulo === dimensionTitulo);
                    if (!dim) return false;
                    if (!Array.isArray(dim.derechosTitulos)) return true;
                    return dim.derechosTitulos.some(d => selectedDerechos.includes(d));
                });
            }

            state.hasAppliedFilters = true;
            state.allIndicatorsCache = null;
            state.isAllSelected = false;
        },
        clearFilters: (state) => {
            const allRights = state.filterOptions?.derechos.map(d => d.titulo) || [];

            state.derechos = allRights;
            state.tiposIndicadores = [];
            state.dimensiones = [];
            state.ods = [];
            state.priorityGroups = [];
            state.variables = [];

            state.initialFilters = {
                derechos: allRights,
                tiposIndicadores: [],
                dimensiones: [],
                ods: [],
                priorityGroups: [],
                variables: []
            };
            state.hasAppliedFilters = false;
            state.allIndicatorsCache = null;
            state.isAllSelected = false;
        },
        toggleIndicatorSelection: (state, action: PayloadAction<OpenDataIndicator>) => {
            const indicator = action.payload;
            const index = state.selectedIndicators.findIndex(i => i.id === indicator.id);
            if (index >= 0) {
                state.selectedIndicators.splice(index, 1);
            } else {
                state.selectedIndicators.push(indicator);
            }
            state.isAllSelected = false;
        },
        selectAllIndicators: (state, action: PayloadAction<OpenDataIndicator[]>) => {
            state.selectedIndicators = [...action.payload];
            state.isAllSelected = true;
        },
        clearSelections: (state) => {
            state.selectedIndicators = [];
            state.isAllSelected = false;
        },
        setFilterOptions: (state, action: PayloadAction<FilterOptions>) => {
            state.filterOptions = action.payload;
        },
        updateInitialFilters: (state) => {
            state.initialFilters = {
                derechos: [...state.derechos],
                tiposIndicadores: [...state.tiposIndicadores],
                dimensiones: [...state.dimensiones],
                ods: [...state.ods],
                priorityGroups: [...state.priorityGroups],
                variables: [...state.variables]
            };
            state.hasAppliedFilters = true;
        },
        initializeFiltersFromUrl: (state, action: PayloadAction<Partial<FilterState>>) => {
            const keys: (keyof Pick<FilterState, 'derechos' | 'tiposIndicadores' | 'dimensiones' | 'priorityGroups' | 'ods' | 'variables'>)[] = [
                "derechos",
                "tiposIndicadores",
                "dimensiones",
                "ods",
                "priorityGroups",
                "variables"
            ];

            keys.forEach((key) => {
                const value = action.payload[key];

                if (value !== undefined) {
                    state[key] = [...value];
                    state.initialFilters[key] = [...value];
                }
            });
            state.allIndicatorsCache = null;
            state.isAllSelected = false;
        },
        clearAllIndicatorsCache: (state) => {
            state.allIndicatorsCache = null;
        }
    },

    extraReducers: (builder) => {
        builder.addCase(getFilterOptions.fulfilled, (state, action) => {
            const payload = action.payload;
            if (payload) {
                state.filterOptions = payload;
                //Precargar derechos
                if (payload.derechos) {
                    const derechosIniciales = payload.derechos.map(d => d.titulo);
                    state.derechos = derechosIniciales;
                    state.initialFilters.derechos = [...derechosIniciales];
                }
            }
        });
        builder.addCase(getAllIndicatorsForDownload.pending, (state) => {
            state.isLoadingAllIndicators = true;
        });

        builder.addCase(getAllIndicatorsForDownload.fulfilled, (state, action) => {
            state.selectedIndicators = action.payload;
            state.allIndicatorsCache = action.payload;
            state.isLoadingAllIndicators = false;
            state.isAllSelected = true;
        });

        builder.addCase(getAllIndicatorsForDownload.rejected, (state) => {
            state.isLoadingAllIndicators = false;
            state.selectedIndicators = [];
        });
    },
});

export const {
    updateFilter,
    clearFilters,
    toggleIndicatorSelection,
    selectAllIndicators,
    clearSelections,
    setFilterOptions,
    initializeFiltersFromUrl,
    updateInitialFilters
} = openDataFiltersSlice.actions;

export default openDataFiltersSlice.reducer;